import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, TextField, Button } from "@mui/material";
import { editPartner } from "../../services/partnerInformation"; // Import the update function
import { PartnerListItem } from "../../data/partnerData";
import "../admin/Admin.css";

interface EditPartnerModalProps {
    isOpen: boolean;
    onClose: () => void;
    partner: PartnerListItem | null; // Existing partner data
    setSnackbarMessage: (message: string) => void;
    setSnackbarOpen: (open: boolean) => void;
    setDrawerOpen: (open: boolean) => void;
    refreshPartners: () => void;
}

// Styling similar to the Add Modal
const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const EditPartnerModal: React.FC<EditPartnerModalProps> = ({isOpen, onClose, partner, setSnackbarMessage, setSnackbarOpen, refreshPartners, setDrawerOpen}) => {
    const [partnerData, setPartnerData] = useState<PartnerListItem | null>(partner);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Validation states
    const [errors, setErrors] = useState({
        ContactName: "",
        EmailAddress: "",
        ContactPhone: "",
        Name: "",
        Description: "",
    });

    // Update partner data when modal opens
    useEffect(() => {
        if (partner) {
            setPartnerData(partner);
        }
    }, [partner]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!partnerData) return;
        setPartnerData({ ...partnerData, [e.target.name]: e.target.value });

        // Clear validation error when user types
        setErrors({ ...errors, [e.target.name]: "" });
    };

    const validateFields = () => {
        let newErrors = { ...errors };
        let isValid = true;

        if (!partnerData?.ContactName.trim()) {
            newErrors.ContactName = "Contact name is required.";
            isValid = false;
        }
        if (!partnerData?.EmailAddress.trim()) {
            newErrors.EmailAddress = "Email is required.";
            isValid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(partnerData.EmailAddress)) {
            newErrors.EmailAddress = "Invalid email format.";
            isValid = false;
        }
        if (!partnerData?.ContactPhone.trim()) {
            newErrors.ContactPhone = "Phone number is required.";
            isValid = false;
        } else if (!/^\d+$/.test(partnerData.ContactPhone)) {
            newErrors.ContactPhone = "Phone number must contain only numbers.";
            isValid = false;
        }
        if (!partnerData?.Name.trim()) {
            newErrors.Name = "Company name is required.";
            isValid = false;
        }
        if (!partnerData?.Description.trim()) {
            newErrors.Description = "Description is required.";
            isValid = false;
        } else if (partnerData?.Description && partnerData.Description.length > 500) {
            newErrors.Description = "Description must be under 500 characters.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!partnerData) return;

        if (!validateFields()) return; // Prevent submission if validation fails

        setLoading(true);
        setErrorMessage(null);

        try {
            await editPartner(partnerData); // Call API to update partner
            console.log("Partner updated successfully");

            setDrawerOpen(false);
            // Show success snackbar
            setSnackbarMessage("Partner updated successfully");
            setSnackbarOpen(true);

            // Refresh the partner list
            refreshPartners();

            // Close modal
            onClose();
        } catch (error) {
            console.error("Update Error:", error);
            setErrorMessage("Failed to update partner. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="edit-partner-modal">
            <Box sx={modalStyle}>
                <Typography variant="h6" component="h2">
                    Edit Partner
                </Typography>
                <Box sx={{ mt: 2 }}>
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Contact Name"
                        name="ContactName"
                        value={partnerData?.ContactName || ""}
                        onChange={handleChange}
                        error={Boolean(errors.ContactName)}
                        helperText={errors.ContactName}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Email Address"
                        name="EmailAddress"
                        type="email"
                        value={partnerData?.EmailAddress || ""}
                        onChange={handleChange}
                        error={Boolean(errors.EmailAddress)}
                        helperText={errors.EmailAddress}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Phone Number"
                        name="ContactPhone"
                        value={partnerData?.ContactPhone || ""}
                        onChange={handleChange}
                        error={Boolean(errors.ContactPhone)}
                        helperText={errors.ContactPhone}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Company Name"
                        name="Name"
                        value={partnerData?.Name || ""}
                        onChange={handleChange}
                        error={Boolean(errors.Name)}
                        helperText={errors.Name}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Description"
                        name="Description"
                        multiline
                        rows={3}
                        value={partnerData?.Description || ""}
                        onChange={handleChange}
                        error={Boolean(errors.Description)}
                        helperText={errors.Description}
                    />
                    {errorMessage && <Typography color="error">{errorMessage}</Typography>}
                </Box>
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                    <Button onClick={onClose} sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" className="editOrAdd" disabled={loading}>
                        {loading ? "Updating..." : "Save Changes"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default EditPartnerModal;
